import { CopyField } from '@eb/eds-copy-field';
import { IconButton } from '@eb/eds-icon-button';
import { ShareIosChunky } from '@eb/eds-iconography';
import { Modal } from '@eb/eds-modal';
import { withTooltip } from '@eb/eds-tooltip';
import { GenericLazyString } from '@eb/i18n';
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { trackShareAction } from '../../tracking/trackShareAction';

interface UseDialog {
    closeDialog: Function;
    isDialogOpen: boolean;
    openDialog: (event: React.MouseEvent) => void;
}

const useDialog = (initialMode = false): UseDialog => {
    const [isDialogOpen, setDialogOpen] = useState(initialMode);
    const openDialog = (): void => setDialogOpen(true);
    const closeDialog = (): void => setDialogOpen(false);

    return {
        closeDialog,
        isDialogOpen,
        openDialog,
    };
};

export interface ShareModalProps {
    title: GenericLazyString;
    inputLabel: GenericLazyString;
    url: string;
    tooltipTitle: GenericLazyString;
    tooltipText: GenericLazyString;
    trackingCategory: string;
    isMobile: boolean;
}

export const ShareModal: React.FunctionComponent<ShareModalProps> = ({
    title,
    inputLabel,
    url,
    tooltipTitle,
    tooltipText,
    trackingCategory,
    isMobile,
    children,
}) => {
    const { openDialog, closeDialog, isDialogOpen } = useDialog();

    const openModal = (e: React.MouseEvent) => {
        openDialog(e);
        trackShareAction('open-modal', trackingCategory);
    };

    const modalWrapperId = 'share-modal-wrapper';

    useEffect(() => {
        const modalWrapper = document.createElement('div');
        modalWrapper.setAttribute('id', modalWrapperId);
        document.body.appendChild(modalWrapper);
    }, []);

    const ShareModal = () => {
        const domElement = document.getElementById(modalWrapperId);

        if (isDialogOpen) {
            return ReactDOM.createPortal(
                <Modal
                    title={title}
                    onClose={closeDialog}
                    isShown={isDialogOpen}
                    noMinHeight={true}
                    roleIsDialog={true}
                >
                    <section className="eds-align--center eds-l-pad-vert-8">
                        <div className="eds-g-cell-8-12">
                            <div className="eds-align--center eds-l-pad-bot-8">
                                {children}
                            </div>
                            <CopyField
                                inputFieldId="shareUrlField"
                                label={inputLabel}
                                value={url}
                            />
                        </div>
                    </section>
                </Modal>,
                domElement as HTMLInputElement,
            );
        }
        return null;
    };

    if (isMobile) {
        return (
            <>
                <IconButton
                    iconType={<ShareIosChunky />}
                    onClick={openModal}
                    title={tooltipTitle}
                />
                <ShareModal />
            </>
        );
    } else {
        const IconWithTooltip = withTooltip(IconButton);
        return (
            <>
                <IconWithTooltip
                    iconType={<ShareIosChunky />}
                    onClick={openModal}
                    title={tooltipTitle}
                    tooltipId="tooltip-share-modal-button"
                    tooltipText={tooltipText}
                />
                <ShareModal />
            </>
        );
    }
};
